import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { MapPin, Calendar as CalendarIcon, Filter, Download, Clock, BookmarkPlus, BookmarkCheck } from "lucide-react";

const AGENDA_KEY = "summit_my_agenda";

function formatTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return iso; }
}
function duration(startIso, endIso) {
  try {
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    const mins = Math.round(ms / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h ? `${h}h${m ? ` ${m}m` : ""}` : `${m} min`;
  } catch { return ""; }
}

// SESSION_KIND → display metadata
const KIND_LABEL = {
  meal: "Networking", opening: "Welcome", closing: "Closing",
  fireside: "Sourcing Fireside Chat", breakout: "Educational Session",
  networking: "Networking", session: "Educational Session", showcase: "Prime Supplier Showcase",
};
const KIND_CATEGORY = {
  opening: "Welcome",
  session: "Educational Session",
  fireside: "Sourcing Fireside Chat",
  meal: "Networking",
  networking: "Networking",
  showcase: "Prime Supplier Showcase & Business Matchmaking",
  closing: "Closing",
  breakout: "Educational Session",
};

// Overrides for premium executive framing — speaker attribution intentionally
// suppressed platform-wide until the final lineup is confirmed.
function displayForSession(s) {
  if (s.kind === "fireside") {
    return {
      title: s.title || "Sourcing Fireside Chat",
      subtitle: null,
      description:
        "Fireside conversation on procurement leadership, strategic sourcing, supplier innovation, and supplier diversity.",
    };
  }
  if (s.kind === "meal") {
    return {
      title: s.title,
      subtitle: null,
      description:
        "Hosted networking with Travel + Leisure leaders, Prime Suppliers, and Small Business Enterprises.",
    };
  }
  if (s.kind === "showcase") {
    return {
      title: s.title,
      subtitle: null,
      description:
        "Prime Suppliers will showcase products, services, and innovations while participating in scheduled Business Matchmaking appointments and open networking throughout the afternoon.",
    };
  }
  return { title: s.title, subtitle: null, description: s.description };
}

function toICSDate(iso) {
  try { return new Date(iso).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"; }
  catch { return ""; }
}

function icsFor(s) {
  const uid = `${s.id}@summit.displayamerica.com`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Display America//Strategic Supplier Summit//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${toICSDate(new Date().toISOString())}`,
    `DTSTART:${toICSDate(s.start)}`,
    `DTEND:${toICSDate(s.end)}`,
    `SUMMARY:${(s.title || "").replace(/,/g, "\\,")}`,
    `DESCRIPTION:${((displayForSession(s).description || "").replace(/\n/g, "\\n").replace(/,/g, "\\,"))}`,
    `LOCATION:${(s.location || "").replace(/,/g, "\\,")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return new Blob([lines.join("\r\n")], { type: "text/calendar" });
}

function googleCalLink(s) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: displayForSession(s).title,
    dates: `${toICSDate(s.start)}/${toICSDate(s.end)}`,
    details: displayForSession(s).description || "",
    location: s.location || "",
  });
  return `https://www.google.com/calendar/render?${params.toString()}`;
}
function outlookCalLink(s) {
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    startdt: s.start,
    enddt: s.end,
    subject: displayForSession(s).title,
    body: displayForSession(s).description || "",
    location: s.location || "",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

function useMyAgenda() {
  const [ids, setIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(AGENDA_KEY) || "[]"); } catch { return []; }
  });
  const toggle = (id) => setIds((cur) => {
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    localStorage.setItem(AGENDA_KEY, JSON.stringify(next));
    return next;
  });
  return [ids, toggle];
}

export default function SchedulePage({ breakoutsOnly = false }) {
  const [sessions, setSessions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [event, setEvent] = useState(null);
  const [filter, setFilter] = useState("");
  const [myAgenda, toggleAgenda] = useMyAgenda();

  useEffect(() => {
    api.get("/sessions", { params: breakoutsOnly ? { kind: "session" } : {} })
      .then(({ data }) => setSessions(data.sessions || []));
    api.get("/event").then(({ data }) => { setEvent(data); setTopics(data.breakout_topics || []); });
  }, [breakoutsOnly]);

  const filtered = useMemo(() => {
    if (!filter) return sessions;
    return sessions.filter((s) => s.topic === filter);
  }, [sessions, filter]);

  // Group Executive Education (session kind) into ONE block per time slot
  const groupedForDisplay = useMemo(() => {
    if (breakoutsOnly) return filtered.map((s) => ({ type: "single", session: s }));
    const rows = [];
    const consumed = new Set();
    for (const s of filtered) {
      if (consumed.has(s.id)) continue;
      if (s.kind === "session") {
        const sameSlot = filtered.filter((x) => x.kind === "session" && x.start === s.start && x.end === s.end);
        sameSlot.forEach((x) => consumed.add(x.id));
        rows.push({ type: "education-group", sessions: sameSlot });
      } else {
        consumed.add(s.id);
        rows.push({ type: "single", session: s });
      }
    }
    return rows;
  }, [filtered, breakoutsOnly]);

  const dayLabel = event?.date || "Friday, September 11, 2026";

  return (
    <Layout>
      <section className="container-summit py-12 md:py-16">
        {breakoutsOnly && (
          <div className="overline mb-4"><span className="accent-rule" />Educational Sessions</div>
        )}
        <h1 className="text-4xl sm:text-5xl">{breakoutsOnly ? "Educational Sessions" : "Event Agenda"}</h1>
        <p className="mt-4 text-zinc-500 max-w-2xl">
          {breakoutsOnly
            ? "Educational sessions led by Travel + Leisure procurement, sourcing, and category leaders."
            : "Review the complete agenda for the Strategic Supplier Summit, including educational sessions, networking lunch, the Supplier Showcase, and Business Matchmaking opportunities. Individual sessions can be added to your calendar."}
        </p>

        {breakoutsOnly && (
          <div className="mt-8 flex items-center gap-3 overflow-x-auto no-scrollbar">
            <Filter size={14} className="text-zinc-400" />
            <button onClick={() => setFilter("")} data-testid="topic-all" className={`pill ${!filter ? "bg-black text-white border-black" : ""}`}>All Topics</button>
            {topics.map((t) => (
              <button key={t} onClick={() => setFilter(t)} data-testid={`topic-${t.slice(0,12)}`}
                className={`pill whitespace-nowrap ${filter === t ? "bg-black text-white border-black" : ""}`}>{t}</button>
            ))}
          </div>
        )}

        <div className="mt-10">
          <div className="flex items-baseline gap-3 mb-6">
            <h2 className="serif text-2xl" data-testid="schedule-day-label">{dayLabel}</h2>
            <span className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              {breakoutsOnly ? `${filtered.length} educational sessions` : `${groupedForDisplay.length} program blocks`}
            </span>
          </div>
          <div className="border-l border-zinc-200">
            {groupedForDisplay.map((row, idx) => (
              <div key={idx}>
                {row.type === "single" && (
                  <SessionRow s={row.session} inAgenda={myAgenda.includes(row.session.id)} onAgenda={() => toggleAgenda(row.session.id)} />
                )}
                {row.type === "education-group" && (
                  <EducationGroup sessions={row.sessions} myAgenda={myAgenda} onAgenda={toggleAgenda} />
                )}
              </div>
            ))}
          </div>
          {groupedForDisplay.length === 0 && <p className="text-zinc-500 text-sm">No sessions yet.</p>}
        </div>
      </section>
    </Layout>
  );
}

function SessionRow({ s, inAgenda, onAgenda }) {
  const view = displayForSession(s);
  return (
    <article className="pl-6 pr-4 py-6 border-b border-zinc-100 last:border-0 hover:bg-zinc-50/60 transition-colors group relative" data-testid={`session-row-${s.id}`}>
      <span className="absolute -left-[5px] top-8 w-2.5 h-2.5 rounded-full bg-white border border-black" />
      <div className="grid md:grid-cols-[150px_1fr_auto] gap-4 md:gap-8 items-start">
        <div>
          <div className="text-sm font-medium">{formatTime(s.start)} — {formatTime(s.end)}</div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-1">{KIND_LABEL[s.kind] || s.kind}</div>
        </div>
        <div>
          <h3 className="text-lg sm:text-xl">{view.title}</h3>
          {view.subtitle && <div className="text-sm text-[#A39171] mt-1 italic">{view.subtitle}</div>}
          {view.description && <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{view.description}</p>}
          <SessionMeta s={s} />
          {/* Fireside panelist list intentionally suppressed platform-wide until final lineup is confirmed. */}
        </div>
        <SessionActions s={s} inAgenda={inAgenda} onAgenda={onAgenda} />
      </div>
    </article>
  );
}

function EducationGroup({ sessions, myAgenda, onAgenda }) {
  const first = sessions[0];
  return (
    <article className="pl-6 pr-4 py-6 border-b border-zinc-100 hover:bg-zinc-50/60 transition-colors group relative" data-testid="education-group">
      <span className="absolute -left-[5px] top-8 w-2.5 h-2.5 rounded-full bg-white border border-black" />
      <div className="grid md:grid-cols-[150px_1fr_auto] gap-4 md:gap-8 items-start">
        <div>
          <div className="text-sm font-medium">{formatTime(first.start)} — {formatTime(first.end)}</div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-1">Educational Session</div>
        </div>
        <div>
          <h3 className="text-lg sm:text-xl">Educational Sessions</h3>
          <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
            Educational conversations led by Travel + Leisure and industry experts.
          </p>
          <div className="mt-4 grid gap-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#A39171]">Topics Include</div>
            <ul className="grid sm:grid-cols-3 gap-3 mt-1">
              {sessions.map((s) => (
                <li key={s.id} className="border border-zinc-200 p-3" data-testid={`edu-topic-${s.id}`}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{s.topic || "Session"}</div>
                  <div className="text-sm font-medium mt-1">{(s.title || "").replace(/^Educational Session · /, "").replace(/^Executive Education · /, "")}</div>
                  {/* Speaker names intentionally suppressed until lineup confirmed. */}
                </li>
              ))}
            </ul>
          </div>
          <SessionMeta s={first} category="Educational Session" />
        </div>
        <SessionActions s={first} inAgenda={myAgenda.includes(first.id)} onAgenda={() => onAgenda(first.id)} />
      </div>
    </article>
  );
}

function SessionMeta({ s, category }) {
  const cat = category || KIND_CATEGORY[s.kind] || null;
  return (
    <div className="mt-4 flex flex-wrap gap-4 text-xs text-zinc-500">
      <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {duration(s.start, s.end)}</span>
      {s.location && <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {s.location}</span>}
      {/* Speaker attribution suppressed platform-wide until final lineup confirmed. */}
      {cat && <span className="inline-flex items-center gap-1.5 text-[#A39171] uppercase tracking-[0.14em]">{cat}</span>}
    </div>
  );
}

function SessionActions({ s, inAgenda, onAgenda }) {
  const downloadIcs = () => {
    const blob = icsFor(s);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(s.title || "session").replace(/[^a-z0-9]/gi, "-").toLowerCase()}.ics`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="flex flex-col gap-2 text-xs md:opacity-0 md:group-hover:opacity-100 transition-opacity">
      <button onClick={onAgenda} data-testid={`agenda-${s.id}`}
        className={`inline-flex items-center gap-2 h-9 px-3 border ${inAgenda ? "bg-[#A39171] text-white border-[#A39171]" : "border-zinc-300 hover:border-black"} uppercase tracking-[0.14em]`}>
        {inAgenda ? <BookmarkCheck size={12} /> : <BookmarkPlus size={12} />}
        {inAgenda ? "In My Agenda" : "Add to My Agenda"}
      </button>
      <a href={googleCalLink(s)} target="_blank" rel="noreferrer" data-testid={`add-google-${s.id}`}
        className="inline-flex items-center gap-2 h-9 px-3 border border-zinc-300 hover:border-black uppercase tracking-[0.14em]">
        <CalendarIcon size={12} /> Add to Google
      </a>
      <a href={outlookCalLink(s)} target="_blank" rel="noreferrer" data-testid={`add-outlook-${s.id}`}
        className="inline-flex items-center gap-2 h-9 px-3 border border-zinc-300 hover:border-black uppercase tracking-[0.14em]">
        <CalendarIcon size={12} /> Add to Outlook
      </a>
      <button onClick={downloadIcs} data-testid={`add-ics-${s.id}`}
        className="inline-flex items-center gap-2 h-9 px-3 border border-zinc-300 hover:border-black uppercase tracking-[0.14em]">
        <Download size={12} /> Download .ics
      </button>
    </div>
  );
}
